import { Component, OnInit } from '@angular/core';

import { StateService } from '@uirouter/angular';
import { TranslateService } from '@ngx-translate/core';

import { BootstrapContext } from 'rev-shared/bootstrap/BootstrapContext';
import { CsrfTokenCookie } from 'rev-shared/security/Tokens';
import { LoginComponent } from 'rev-shared/security/components/Login.Component';
import { LoginRedirectService, SSOLoginPath } from 'rev-shared/security/LoginRedirect.Service';
import { SecurityContextService } from 'rev-shared/security/SecurityContext.Service';
import { UserAuthenticationService } from 'rev-shared/security/UserAuthentication.Service';
import { UserContextService } from 'rev-shared/security/UserContext.Service';
import { decodeQueryString, addQueryParams } from 'rev-shared/util/Util.Service';
import { getCookie } from 'rev-shared/util/CookieUtil';

import { isSsoConsume } from '../isSsoConsume';
import { requestStorageAccess, hasStorageAccess } from '../EmbedStorageCheck';

import authLayoutStyles from 'rev-shared/ui/authLayout/VbAuthLayout.module.less';
import styles from '../Embed.module.less';

const query = decodeQueryString();
const debugParam = 'debug' in query ? `?debug=${query.debug}` : '';
const embedLoginUrl = `/embed/login${debugParam}`;

@Component({
	selector: 'embed-login',
	templateUrl: './EmbedLogin.Component.html',
	standalone: false
})
export class EmbedLoginComponent extends LoginComponent implements OnInit {
	public readonly authLayoutStyles = authLayoutStyles;
	public readonly styles = styles;

	private hasStorageAccess: boolean = false;
	private enablePopupAuth: boolean;
	public showLoginForm: boolean;
	private ssoEnabled: boolean;
	private ssoLoginUrl: string;
	public showPopupFallback: boolean;
	public isAuthenticated: boolean;
	public fullscreenPlayerUrl: string;
	public loginPopup: WindowProxy;

	public get showThumbnail() {
		return !this.showLoginForm && !this.showPopupFallback;
	}

	constructor(
		$state: StateService,
		LoginRedirectService: LoginRedirectService,
		SecurityContext: SecurityContextService,
		UserContext: UserContextService,
		TranslateService: TranslateService,
		private readonly UserAuthenticationService: UserAuthenticationService
	) {
		super($state, LoginRedirectService, SecurityContext, UserContext, TranslateService);
	}

	public ngOnInit(): void {
		console.log('ngOnInit Embed Login');
		super.ngOnInit();

		if(isSsoConsume){
			console.log('Came from sso but user was not logged in');
			this.showPopupFallback = true;
		}

		const [url] = window.location.href.split('#');
		this.fullscreenPlayerUrl = addQueryParams(url, { autoplay: true });

		this.enablePopupAuth = 'popupAuth' in query ? (query.popupAuth === 'true') : BootstrapContext.enableEmbedPopupAuth;
		this.showLoginForm = !isSsoConsume && (
			window.parent === window //Show login form immediately if loaded in its own tab, or if coming from placeholder
			|| query.placeholder === 'none');

		if(this.UserContext.ssoEnabled()) {
			this.ssoEnabled = true;

			this.ssoLoginUrl = `${SSOLoginPath}?fwdUrl=${embedLoginUrl}&app=embedLogin`;
		}
	}

	protected onUserAuthenticated(): void {
		console.log('onUserAuthenticated');
		Promise.resolve(this.hasStorageAccess || hasStorageAccess())
			.then(() => {
				console.log('onUserAuthenticated, hasStorageAccess');
				super.onUserAuthenticated();
			})
			.catch(() => {
				console.log('No Storage Access, waiting for user click');
				this.isAuthenticated = true;
				this.showLoginForm = false;
				this.showPopupFallback = false;
			});
	}

	public onLoginError(e: any) {
		if(e.sessionNotStable){
			this.showLoginForm = false;
			this.showPopupFallback = true;
			return;
		}
		super.onLoginError(e);
	}

	public openPopupPlayer(): void {
		window.open(this.fullscreenPlayerUrl, '_blank');
	}

	public openPopupLogin(): void {
		if(this.loginPopup && !this.loginPopup.closed) {
			this.loginPopup.focus();
			return;
		}
		this.loginPopup = openWindow(
			this.ssoEnabled ? this.ssoLoginUrl : embedLoginUrl,
			600, 600);
	}

	public onThumbnailClick(): void {
		history.replaceState(null, null, getLocationWithAutoplay());

		(this.enablePopupAuth ? requestStorageAccess() : hasStorageAccess())
			.then(() => {
				this.hasStorageAccess = true;
			})
			.finally(() => {
				if(this.isAuthenticated) {
					console.log('Thumbnail Click, Fwd to video');
					super.onUserAuthenticated();
					return;
				}
				if(this.sessionCheck()) {
					return;
				}

				this.testCookieThenOpenLogin();
			});
	}

	private sessionCheck(): boolean {
		if(getCookie(CsrfTokenCookie)) {
			console.log('Cookie access ok, check session');
			this.hasStorageAccess = true;
			this.UserAuthenticationService.checkSessionHealth()
				.then(() => {
					//Frame was loaded without cookies, force a refresh.
					console.log('Session ok, fwding');
					this.redirectToLandingUrl(true);
				})
				.catch(() => {
					console.log('Session not valid, needs login');
					this.tryOpenLogin();
				});
			return true;
		}
	}

	private tryOpenLogin(): void {

		if(this.enablePopupAuth || this.ssoEnabled) {
			console.log('Thumbnail Click, opening popup auth');
			this.openPopupLogin();
			return;
		}

		console.log('Storage ok. show inline login form');
		this.showLoginForm = true;
	}

	private testCookieThenOpenLogin(): void {
		this.testCookie()
			.then(() => this.tryOpenLogin())
			.catch(e => {
				console.log('Paritioned cookie access denied, falling back to session storage');

				Promise.resolve(this.hasStorageAccess || requestStorageAccess())
					.then(() => {
						this.hasStorageAccess = true;
						if(this.sessionCheck()) {
							return;
						}

						this.testCookie()
							.then(() => this.tryOpenLogin())
							.catch(e => {
								console.log('Storage access denied, falling back to popup player', e);
								this.showPopupFallback = true;
								this.openPopupPlayer();
							});
					})
					.catch(e => {
						console.log('Storage access denied, falling back to popup player', e);
						this.showPopupFallback = true;
						this.openPopupPlayer();
					});
			});
	}

	private testCookie(): Promise<void> {
		if(document.cookie || !(document as any).requestStorageAccess) {
			return Promise.resolve();
		}

		return this.UserAuthenticationService.testSetCookie();
	}
}

function openWindow(url: string, w: number, h: number): WindowProxy {
	const y = (screen.availHeight - h) / 2;
	const x = (screen.availWidth - w) / 2;
	const params = `location,resizable,width=${w},height=${h},top=${y},left=${x}`;
	console.log('open login: ', params);
	return window.open(url, '', params);
}

function getLocationWithAutoplay(): string {
	return addQueryParams(location.pathname + location.search, {
		autoplay: true
	});
}
